import type { ApiErrorBody } from './types'

/**
 * Fetch wrapper for the Fruuit Engage API (ARCHITECTURE §7):
 * - cookie session (`credentials: 'include'`)
 * - every mutation carries an `X-Request-Id` (D-14) and the `fe_csrf` cookie echoed as
 *   `X-CSRF-Token` (double-submit CSRF)
 * - non-2xx responses become a typed `ApiError` parsed from the D-15 error body
 * - 401 sends the browser to `/signin?next=<current path>`
 */

export const API_BASE = '/api/v1'
export const CSRF_COOKIE = 'fe_csrf'
export const REQUEST_ID_PATTERN = /^[A-Za-z0-9._:-]{8,120}$/

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

/** ULID-style id: 10 chars of millisecond time + 16 chars of randomness (Crockford base32). */
export function newRequestId(now: number = Date.now()): string {
  let time = ''
  let remaining = now
  for (let i = 0; i < 10; i++) {
    time = CROCKFORD.charAt(remaining % 32) + time
    remaining = Math.floor(remaining / 32)
  }
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  let random = ''
  for (const byte of bytes) random += CROCKFORD.charAt(byte % 32)
  return time + random
}

export function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  for (const part of document.cookie.split(';')) {
    const [rawKey, ...rest] = part.trim().split('=')
    if (rawKey === name) return decodeURIComponent(rest.join('='))
  }
  return undefined
}

/** Path → first message, from zod issues in `details.issues` (422). */
export type FieldErrors = Record<string, string>

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly requestId: string | undefined
  readonly details: unknown
  readonly fieldErrors: FieldErrors

  constructor(init: {
    status: number
    code: string
    message: string
    requestId?: string
    details?: unknown
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.requestId = init.requestId
    this.details = init.details
    this.fieldErrors = extractFieldErrors(init.details)
  }
}

function extractFieldErrors(details: unknown): FieldErrors {
  const errors: FieldErrors = {}
  const issues =
    details && typeof details === 'object' && 'issues' in details
      ? (details as { issues: unknown }).issues
      : details
  if (!Array.isArray(issues)) return errors
  for (const issue of issues) {
    if (!issue || typeof issue !== 'object') continue
    const { path, message } = issue as { path?: unknown; message?: unknown }
    const key = Array.isArray(path) ? path.join('.') : typeof path === 'string' ? path : null
    if (key !== null && typeof message === 'string' && !(key in errors)) errors[key] = message
  }
  return errors
}

export function isApiError(error: unknown, ...codes: string[]): error is ApiError {
  return error instanceof ApiError && (codes.length === 0 || codes.includes(error.code))
}

/** Readable message for any thrown value (FORM-027: never raw JSON). */
export function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return 'Something went wrong. Please try again.'
}

/** Same-origin relative path only (blocks `//evil`, `/\evil` and absolute URLs). */
export function safeNext(next: string | null | undefined): string | null {
  if (!next?.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return null
  return next
}

export function signInUrl(next?: string): string {
  const target = safeNext(next)
  return target && target !== '/' ? `/signin?next=${encodeURIComponent(target)}` : '/signin'
}

function defaultUnauthorized() {
  const { pathname, search } = window.location
  if (pathname.startsWith('/signin')) return
  window.location.assign(signInUrl(pathname + search))
}

let onUnauthorized: () => void = defaultUnauthorized

/** Override the 401 behaviour (tests); `null` restores the redirect to sign-in. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler ?? defaultUnauthorized
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
export type QueryParams = Record<string, string | number | boolean | null | undefined>

export interface RequestOptions {
  method?: HttpMethod
  body?: unknown
  query?: QueryParams
  signal?: AbortSignal
  /** Reuse an id to make a retry replay the original command (D-14). Generated if omitted. */
  requestId?: string
  /** Default true. Auth bootstrap calls (`/me`) turn it off and handle 401 themselves. */
  redirectOnUnauthorized?: boolean
}

export function buildUrl(path: string, query?: QueryParams): string {
  const base = path.startsWith('/api/') || path === '/healthz' ? path : `${API_BASE}${path}`
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `${base}?${qs}` : base
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (method !== 'GET') {
    const requestId = options.requestId ?? newRequestId()
    if (!REQUEST_ID_PATTERN.test(requestId)) throw new Error(`Invalid request id: ${requestId}`)
    headers['X-Request-Id'] = requestId
    const csrf = readCookie(CSRF_COOKIE)
    if (csrf) headers['X-CSRF-Token'] = csrf
  }

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      credentials: 'include',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError({
      status: 0,
      code: 'network_error',
      message: "We couldn't reach Fruuit Engage. Check your connection and try again.",
    })
  }

  if (!response.ok) {
    const error = await toApiError(response)
    if (response.status === 401 && options.redirectOnUnauthorized !== false) onUnauthorized()
    throw error
  }
  if (response.status === 204) return undefined as T
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

async function toApiError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as Partial<ApiErrorBody>
    const error = body.error
    if (error && typeof error.code === 'string' && typeof error.message === 'string') {
      return new ApiError({
        status: response.status,
        code: error.code,
        message: error.message,
        requestId: error.request_id,
        details: error.details,
      })
    }
  } catch {
    // Not JSON: fall through to a generic error.
  }
  return new ApiError({
    status: response.status,
    code: response.status === 401 ? 'unauthorized' : 'unexpected_response',
    message: `The request failed (HTTP ${response.status}).`,
  })
}

type BodyOptions = Omit<RequestOptions, 'method' | 'body'>

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body' | 'requestId'>) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: BodyOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: BodyOptions) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: BodyOptions) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: BodyOptions) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
}
